import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";

// Icons
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";
import PersonIcon from "@material-ui/icons/Person";
import ContactMailIcon from "@material-ui/icons/ContactMail";
import SchoolIcon from "@material-ui/icons/School";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import CardFooter from "components/Card/CardFooter.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";

import api from "api/index";
import CustomDatePicker from "components/DatePicker/CustomDatePicker.jsx";
import { primaryColor, grayColor, hexToRgb } from "assets/jss/material-dashboard-pro-react.jsx";

const styles = {
  errorMsg: { color: "#f44336", fontSize: "13px", marginBottom: "8px" },
  successMsg: { color: "#4caf50", fontSize: "13px", marginBottom: "8px" },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    marginTop: "24px",
    marginBottom: "16px",
    paddingBottom: "8px",
    borderBottom: "1px solid " + grayColor[15],
    "& svg": {
      marginRight: "8px",
      color: primaryColor[0]
    }
  },
  sectionTitle: {
    color: "#1a237e",
    fontWeight: "800",
    fontSize: "15px",
    margin: 0,
    textTransform: "uppercase",
    letterSpacing: "0.5px"
  },
  formControl: { width: "100%", marginTop: "16px" },
  heroSection: {
    background: "linear-gradient(60deg, #1a237e, #3949ab)",
    padding: "30px 40px",
    borderRadius: "6px",
    marginBottom: "-40px",
    position: "relative",
    zIndex: 3,
    boxShadow: "0 4px 20px 0 rgba(0, 0, 0,.14), 0 7px 10px -5px rgba(26, 35, 126, 0.4)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  heroTitle: {
    color: "#FFFFFF",
    margin: "0",
    fontWeight: "800",
    fontSize: "1.5rem"
  },
  heroSubtitle: {
    color: "rgba(255, 255, 255, 0.8)",
    margin: "5px 0 0",
    fontSize: "0.9rem"
  },
  cardMain: {
    marginTop: "0",
    paddingTop: "30px"
  },
  actionButtons: {
    marginTop: "20px",
    padding: "20px",
    borderTop: "1px solid " + grayColor[15],
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px"
  },
  selectLabel: {
    color: grayColor[3] + " !important",
    fontWeight: "400",
    fontSize: "14px",
    lineHeight: "1.42857",
    letterSpacing: "unset"
  }
};

const GENDERS = ["Male", "Female"];
const NATIONALITIES = ["Ugandan", "Kenyan", "Tanzanian", "Rwandan", "Other"];

class StudentForm extends Component {
  state = {
    form: {
      firstName: "", lastName: "", dob: "", gender: "",
      nationality: "Ugandan", email: "", phone: "",
      programmeId: "", yearOfStudy: "1", status: "active", enrollmentDate: ""
    },
    programmes: [],
    loading: false, saving: false,
    error: null, success: null,
    editingId: null
  };

  componentDidMount() {
    const { id } = this.props.match.params;
    this.fetchProgrammes().then(() => {
      if (id) this.loadStudent(id);
    });
  }

  fetchProgrammes = async () => {
    try {
      const res = await api.get("/programmes?limit=100");
      const d = res.data.data || res.data;
      this.setState({ programmes: Array.isArray(d) ? d : d.data || [] });
    } catch (e) { /* non-fatal */ }
  };

  loadStudent = async (id) => {
    this.setState({ loading: true });
    try {
      const res = await api.get(`/students/${id}`);
      const s = res.data.data || res.data;
      this.setState({
        editingId: id,
        loading: false,
        form: {
          firstName: s.firstName || "",
          lastName: s.lastName || "",
          dob: s.dob || "",
          gender: s.gender || "",
          nationality: s.nationality || "Ugandan",
          email: s.email || "",
          phone: s.phone || "",
          programmeId: s.programme?.id || s.programmeId || "",
          yearOfStudy: String(s.yearOfStudy || "1"),
          status: s.status || "active",
          enrollmentDate: s.enrollmentDate || ""
        }
      });
    } catch (e) {
      this.setState({ error: "Could not load student.", loading: false });
    }
  };

  handleChange = field => e => {
    this.setState(prev => ({
      form: { ...prev.form, [field]: e.target.value },
      error: null, success: null
    }));
  };

  handleSubmit = async e => {
    e.preventDefault();
    const { form, editingId } = this.state;
    if (!form.firstName || !form.lastName || !form.programmeId || !form.gender) {
      this.setState({ error: "First name, last name, programme, and gender are required." });
      return;
    }
    this.setState({ saving: true, error: null });
    try {
      if (editingId) {
        await api.put(`/students/${editingId}`, form);
        this.setState({ success: "Student updated successfully!", saving: false });
      } else {
        await api.post("/students", form);
        this.setState({ success: "Student registered successfully!", saving: false });
      }
      setTimeout(() => this.props.history.push("/bwillis/students"), 1500);
    } catch (err) {
      const msg = err.response?.data?.message || (editingId ? "Failed to update student." : "Failed to register student.");
      const errors = err.response?.data?.errors;
      this.setState({ error: errors ? errors.map(er => er.msg).join(", ") : msg, saving: false });
    }
  };

  render() {
    const { classes, history } = this.props;
    const { form, programmes, saving, error, success, editingId, loading } = this.state;

    const field = (label, key, type = "text", props = {}) => (
      <CustomInput
        labelText={label}
        id={key}
        formControlProps={{ fullWidth: true }}
        inputProps={{ value: form[key], onChange: this.handleChange(key), type, ...props }}
      />
    );

    if (loading) return <div style={{ textAlign: "center", padding: "60px" }}><CircularProgress /></div>;

    return (
      <GridContainer justify="center">
        <GridItem xs={12} md={10} lg={9}>
          <div className={classes.heroSection}>
            <div>
              <h3 className={classes.heroTitle}>
                {editingId ? "Edit Student Details" : "Register New Student"}
              </h3>
              <p className={classes.heroSubtitle}>
                {editingId 
                  ? "Refine and update information for this student profile" 
                  : "Complete the form below to enroll a new student into the system"}
              </p>
            </div>
            <Button color="white" simple justIcon onClick={() => history.goBack()}>
              <ArrowBackIcon />
            </Button>
          </div>

          <Card className={classes.cardMain}>
            <form onSubmit={this.handleSubmit}>
              <CardBody>
                {error && (
                  <div style={{ backgroundColor: "#ffebee", padding: "12px", borderRadius: "4px", marginBottom: "20px", border: "1px solid #ffcdd2" }}>
                    <p className={classes.errorMsg} style={{ margin: 0 }}>⚠ {error}</p>
                  </div>
                )}
                {success && (
                  <div style={{ backgroundColor: "#e8f5e9", padding: "12px", borderRadius: "4px", marginBottom: "20px", border: "1px solid #c8e6c9" }}>
                    <p className={classes.successMsg} style={{ margin: 0 }}>✓ {success}</p>
                  </div>
                )}

                <div className={classes.sectionHeader}>
                  <PersonIcon />
                  <h5 className={classes.sectionTitle}>Personal Information</h5>
                </div>
                
                <GridContainer>
                  <GridItem xs={12} sm={6}>{field("First Name *", "firstName")}</GridItem>
                  <GridItem xs={12} sm={6}>{field("Last Name *", "lastName")}</GridItem>
                  <GridItem xs={12} sm={4}>
                    <CustomDatePicker
                      labelText="Date of Birth"
                      id="dob"
                      formControlProps={{ fullWidth: true }}
                      inputProps={{ value: form.dob, onChange: this.handleChange("dob"), placeholder: "YYYY-MM-DD" }}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={4}>
                    <FormControl className={classes.formControl}>
                      <InputLabel className={classes.selectLabel}>Gender *</InputLabel>
                      <Select value={form.gender} onChange={this.handleChange("gender")}>
                        {GENDERS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </GridItem>
                  <GridItem xs={12} sm={4}>
                    <FormControl className={classes.formControl}>
                      <InputLabel className={classes.selectLabel}>Nationality</InputLabel>
                      <Select value={form.nationality} onChange={this.handleChange("nationality")}>
                        {NATIONALITIES.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </GridItem>
                </GridContainer>

                <div className={classes.sectionHeader}>
                  <ContactMailIcon />
                  <h5 className={classes.sectionTitle}>Contact Details</h5>
                </div>
                
                <GridContainer>
                  <GridItem xs={12} sm={6}>{field("Email Address", "email", "email")}</GridItem>
                  <GridItem xs={12} sm={6}>{field("Phone Number", "phone", "tel")}</GridItem>
                </GridContainer>

                <div className={classes.sectionHeader}>
                  <SchoolIcon />
                  <h5 className={classes.sectionTitle}>Academic Information</h5>
                </div>
                
                <GridContainer>
                  <GridItem xs={12} sm={6}>
                    <FormControl className={classes.formControl}>
                      <InputLabel className={classes.selectLabel}>Programme *</InputLabel>
                      <Select value={form.programmeId} onChange={this.handleChange("programmeId")}>
                        {programmes.length === 0 && <MenuItem value="">— No programmes loaded —</MenuItem>}
                        {programmes.map(p => <MenuItem key={p.id} value={p.id}>{p.name} ({p.code})</MenuItem>)}
                      </Select>
                    </FormControl>
                  </GridItem>
                  <GridItem xs={12} sm={3}>
                    <FormControl className={classes.formControl}>
                      <InputLabel className={classes.selectLabel}>Year of Study</InputLabel>
                      <Select value={form.yearOfStudy} onChange={this.handleChange("yearOfStudy")}>
                        {[1, 2, 3].map(y => <MenuItem key={y} value={String(y)}>Year {y}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </GridItem>
                  <GridItem xs={12} sm={3}>
                    <CustomDatePicker
                      labelText="Enrollment Date"
                      id="enrollmentDate"
                      formControlProps={{ fullWidth: true }}
                      inputProps={{ value: form.enrollmentDate, onChange: this.handleChange("enrollmentDate"), placeholder: "YYYY-MM-DD" }}
                    />
                  </GridItem>
                </GridContainer>

                <div className={classes.actionButtons}>
                  <Button round color="default" onClick={() => history.goBack()} disabled={saving}>
                    <CancelIcon style={{ marginRight: "8px" }} /> Cancel
                  </Button>
                  <Button round color="primary" type="submit" disabled={saving}>
                    {saving ? (
                      <CircularProgress size={20} style={{ color: "#fff" }} />
                    ) : (
                      <>
                        <SaveIcon style={{ marginRight: "8px" }} />
                        {editingId ? "Save Changes" : "Register Student"}
                      </>
                    )}
                  </Button>
                </div>
              </CardBody>
            </form>
          </Card>
        </GridItem>
      </GridContainer>
    );
  }
}

StudentForm.propTypes = { classes: PropTypes.object.isRequired, history: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(StudentForm));
